import { formatMilesCompact, formatPrice } from "@/lib";
import { calculateTotalPayout, MilesProgram } from "@/lib/miles-calculations";
import { cn } from "@/lib/utils";
import { TransactionTypes } from "@/stores";
import { Check, Plane, TicketsPlane } from "lucide-react";
import { Card, CardContent } from "../ui/card";

export function OrderSummary({
  program,
  asUpcoming = false,
  asCompleted = false,
  showPayoutClaim = false,
}: Readonly<{
  program: MilesProgram;
  asUpcoming?: boolean;
  asCompleted?: boolean;
  showPayoutClaim?: boolean;
}>) {
  if (!program) {
    return (
      <Card className="w-full bg-white rounded-md shadow-lg">
        <CardContent className="flex flex-col gap-4 w-full">
          <div className="text-center text-gray-500">
            No program data available
          </div>
        </CardContent>
      </Card>
    );
  }

  if (asCompleted) {
    return (
      <Card className="w-full xl:max-w-3xl mx-auto bg-white rounded-md shadow-lg">
        <CardContent className="flex gap-2 w-full">
          <div className="flex flex-col gap-2 md:gap-4 w-full">
            <div className="flex items-center justify-between ">
              <span className="text-[16px] font-medium">{program.name}</span>
            </div>
            <div className="flex items-center justify-between ">
              <div className="flex items-center gap-2">
                <img src="/order-summary/miles.svg" alt="Miles icon" />
                <span className="text-sm font-medium">
                  {formatMilesCompact(program.sanitizedMiles)}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-[20px] md:text-2xl font-bold">
              {formatPrice({ price: program.totalPayout })}
            </span>

            <Check className="text-accent h-fit w-8" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const transactionType =
    program.buyOrSell === "buy" ? TransactionTypes.BUY : TransactionTypes.SELL;

  const { promoCodeDiscount } = calculateTotalPayout({
    totalMiles: program.sanitizedMiles,
    dynamicRate: program.ratePerMile,
    loyaltyStatus: program.loyaltyStatus,
    transactionType,
    promoCode: program.promoCode,
  });

  return (
    <Card
      className={cn(
        "w-full xl:max-w-3xl mx-auto rounded-md bg-white shadow-lg",
        asUpcoming ? "opacity-85" : "opacity-100",
      )}
    >
      <CardContent
        className={cn(
          "flex flex-col gap-2 w-full",
          showPayoutClaim && "px-5 py-4 text-left xl:px-5 xl:py-0",
        )}
      >
        {showPayoutClaim ? (
          <div className="space-y-3 xl:pb-1">
            <div className="flex items-center gap-2">
              <span className="h-1.5 w-8 rounded-full bg-blue-600 xl:w-9" />
              <span className="h-1.5 w-8 rounded-full bg-blue-600 xl:w-9" />
              <span className="h-1.5 w-8 rounded-full bg-blue-600 xl:w-9" />
              <span className="ml-2 text-[11px] font-bold uppercase tracking-[0.2em] text-gray-400 xl:text-xs">
                Step 3 of 3
              </span>
            </div>

            <div className="xl:hidden">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600">
                Your confirmed payout
              </p>
              <div className="mt-1 text-5xl font-black leading-none tracking-normal text-gray-950">
                {formatPrice({ price: program.totalPayout, fractionDigits: 0 })}
              </div>
              <p className="mt-3 text-sm font-medium text-gray-500">
                for {formatMilesCompact(program.sanitizedMiles)} {program.name}{" "}
                miles
              </p>
            </div>

            <div className="space-y-1 xl:flex xl:items-start xl:justify-between xl:gap-6">
              <div className="min-w-0">
                <h2 className="text-2xl font-bold leading-tight text-gray-950 xl:text-3xl">
                  <span className="hidden xl:inline">Claim your payout</span>
                  <span className="xl:hidden">
                    Claim your payout — one final step
                  </span>
                </h2>
                <p className="text-sm leading-relaxed text-gray-600">
                  One final step, choose how you'd like to be paid and confirm
                  your account details
                </p>
              </div>
              <div className="hidden shrink-0 text-right text-4xl font-black leading-none text-accent xl:block">
                {formatPrice({
                  price: program.totalPayout,
                  fractionDigits: 0,
                })}
              </div>
            </div>
          </div>
        ) : null}

        {showPayoutClaim ? (
          <div className="hidden h-px w-full bg-gray-200/80 xl:block" />
        ) : null}

        <div
          className={cn(
            "flex flex-col gap-2 md:gap-4 w-full text-black xl:text-muted-foreground",
            showPayoutClaim && "hidden xl:flex xl:gap-2 xl:pt-1",
          )}
        >
          <div className="flex items-center justify-between ">
            <div className="flex items-center gap-2">
              <Plane className="w-5 h-5" />
              <span className="text-[16px]">Airline</span>
            </div>
            <div className="flex gap-1 items-center">
              <img
                src={program.logo ?? "/tmm-dark-logo.svg"}
                className="h-8 aspect-square"
                alt={`${program.name} logo`}
              />
              <span className="text-[16px] font-medium">{program.name}</span>
            </div>
          </div>
          <div className="flex items-center justify-between ">
            <div className="flex items-center gap-2">
              <TicketsPlane className="w-5 h-5" />
              <span className="text-[16px]">Miles</span>
            </div>
            <span className="text-sm font-medium">
              {formatMilesCompact(program.sanitizedMiles)}
            </span>
          </div>
          <div className="flex items-center justify-between ">
            <div className="text-sm mb-1">Average Rate</div>
            <div className="text-sm font-medium">
              {formatPrice({
                price: program.ratePerMile * 100,
                fractionDigits: 2,
              })}
              / 100 Miles
            </div>
          </div>
          <div className="flex items-center justify-between ">
            <div className="text-sm mb-1">Account Status</div>
            <div className="text-sm font-medium">
              {program.loyaltyStatus
                ? `${program.loyaltyStatus.name} • +${program.loyaltyStatus.percentage}%`
                : "No loyalty status"}
            </div>
          </div>
          {program.promoCode && promoCodeDiscount !== 0 && (
            <div className="flex items-center justify-between ">
              <div className="text-sm mb-1">
                Promo Code Bonus{" "}
                <span className="text-xs text-muted-foreground">
                  {program.promoCode.name}
                </span>
              </div>
              <div className={`text-sm font-medium`}>
                {formatPrice({
                  price: promoCodeDiscount,
                  fractionDigits: 2,
                })}
              </div>
            </div>
          )}
        </div>

        {!showPayoutClaim ? (
          <div className="flex items-center justify-between border-t pt-2">
            <span className="text-[20px] md:text-lg font-semibold">Total</span>
            <span className="text-[20px] md:text-2xl font-bold">
              {formatPrice({ price: program.totalPayout })}
            </span>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
